import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { db, collection, query, getDocs } from '../firebaseConfig';
import InfoIcon from '@mui/icons-material/Info';
import { Line } from 'react-chartjs-2';
import { useTheme } from '@mui/material/styles';
import Card from '@mui/material/Card';
import MDBox from 'components/MDBox';
import Tooltip from '@mui/material/Tooltip';
import Divider from '@mui/material/Divider';
import AssessmentIcon from '@mui/icons-material/Assessment';
import MDTypography from 'components/MDTypography';

const ScoreGraph = ({ userID, trades, tradeAccountID, showOverallPerformance, tradeAccounts }) => {
  const [scores, setScores] = useState([]);
  const theme = useTheme();
  const iconColor = theme.palette.mode === 'dark' ? theme.palette.grey[200] : theme.palette.grey[600];

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { 
      year: '2-digit',
      month: 'short'
    });
  };

  useEffect(() => {
    const fetchScores = async () => {
      const scoresPath = showOverallPerformance
        ? collection(db, 'Users', userID, 'monthlyScores')
        : collection(db, 'Users', userID, 'tradeAccounts', tradeAccountID, 'monthlyScores');
      const scoresQuery = query(scoresPath);
      const scoreSnapshot = await getDocs(scoresQuery);
  
      const uniqueScoresMap = new Map();
      scoreSnapshot.docs.forEach(doc => {
        const monthDate = new Date(doc.data().month);
        const monthKey = monthDate.toISOString().slice(0, 7);
        if (!uniqueScoresMap.has(monthKey) || 
            new Date(uniqueScoresMap.get(monthKey).month) < monthDate) {
          uniqueScoresMap.set(monthKey, {
            ...doc.data(),
            month: new Date(doc.data().month).toISOString()
          });
        }
      });
  
      const scores = Array.from(uniqueScoresMap.values())
        .sort((a, b) => new Date(a.month) - new Date(b.month));
      setScores(scores);
    };
  
    if (userID && (showOverallPerformance || tradeAccountID)) {
      fetchScores();
    }
  }, [userID, tradeAccountID, showOverallPerformance]);

  const data = {
    labels: scores.map(score => formatDate(score.month)),
    datasets: [
      {
        label: 'Monthly Score',
        data: scores.map(score => score.score),
        fill: false,
        backgroundColor: 'rgb(75, 192, 192)',
        borderColor: 'rgba(75, 192, 192, 0.2)',
      },
    ],
  };

  const chartOptions = {
    maintainAspectRatio: false,
    responsive: true,
    scales: {
      x: {
        type: 'category',
        ticks: {
          autoSkip: true,
          maxTicksLimit: 12,
          maxRotation: 45,
          minRotation: 45
        }
      },
      y: {
        beginAtZero: true,
        min: 0,
        max: 10,
        grid: {
          display: true
        },
        ticks: {
          stepSize: 1
        }
      }
    },
    plugins: {
      legend: {
        display: true
      }
    }
  };

  const tooltipContent = showOverallPerformance
    ? `FX Bullsheet Score measures overall trading performance (0-10) across all accounts.
       Combines win rate, profit consistency, risk management, and efficiency metrics.
       8+ indicates excellent trading strategy, 5-7 shows good performance, 3-4 is average, 1-2 is poor.`
    : `FX Bullsheet Score measures trading performance (0-10) for this account.
       Combines win rate, profit consistency, risk management, and efficiency metrics.
       8+ indicates excellent trading strategy, 5-7 shows good performance, 3-4 is average, 1-2 is poor.`;

  return (
    <Card style={{ minHeight: '300px' }}>
      <MDBox display="flex" flexDirection="column" justifyContent="space-between" pt={1} px={2} height="100%">
        <MDBox display="flex" justifyContent="space-between">
          <MDBox
            variant="gradient"
            bgColor="light"
            color="dark"
            coloredShadow="light"
            borderRadius="xl"
            display="flex"
            justifyContent="center"
            alignItems="center"
            width="4rem"
            height="4rem"
            mt={-3}
          >
            <AssessmentIcon fontSize="medium" color="inherit" />
          </MDBox>
          <MDBox textAlign="right" lineHeight={1.25} mb={4}>
          <MDTypography variant="button" fontWeight="light" color="text" fontFamily="inherit">
  Monthly Score Progress {showOverallPerformance 
    ? '(All Trades)' 
    : `(${tradeAccounts.find(account => account.id === tradeAccountID)?.name || 'Unnamed Account'})`}
</MDTypography>
            <Tooltip 
              title={tooltipContent}
              placement="bottom-end"
              componentsProps={{
                tooltip: {
                  sx: {
                    bgcolor: theme.palette.background.paper,
                    color: theme.palette.text.primary,
                    border: `1px solid ${theme.palette.divider}`,
                    p: 1.5,
                    maxWidth: '250px',
                    fontSize: '0.75rem',
                    lineHeight: 1.5,
                    '& .MuiTooltip-arrow': {
                      color: theme.palette.background.paper,
                    }
                  }
                }
              }}
            >
              <InfoIcon 
                sx={{ 
                  ml: 1, 
                  fontSize: '1rem',
                  color: iconColor,
                  cursor: 'help'
                }} 
              />
            </Tooltip>
          </MDBox>
        </MDBox>
        <Divider />
        <MDBox height="100%">
          <Line data={data} options={chartOptions} />
        </MDBox>
      </MDBox>
    </Card>
  );
};

ScoreGraph.propTypes = {
  userID: PropTypes.string.isRequired,
  trades: PropTypes.array.isRequired,
  tradeAccountID: PropTypes.string.isRequired,
  showOverallPerformance: PropTypes.bool.isRequired,
  tradeAccounts: PropTypes.array.isRequired,
};

export default ScoreGraph;